import React from 'react';
import { FiList, FiSearch, FiCheck } from 'react-icons/fi';
import Modal from '../../../components/Modal';

const RevokeAccessModal = ({
  isOpen,
  onClose,
  selectedAssessment,
  revokeShareDetails,
  revokeLoading,
  revokeSelectedOrgIds,
  revokeSelectedUserIds,
  onRevoke,
  onToggleOrg,
  onToggleUser,
  onSelectAll,
  onDeselectAll,
}) => {
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Revoke Access"
      size="xl"
      footer={
        <>
          <button
            onClick={onClose}
            className="px-4 py-2 border rounded-lg text-sm transition-colors border-[#919191]/40 text-[#53545a] hover:bg-[#919191]/5"
          >
            Cancel
          </button>
          <button
            onClick={onRevoke}
            disabled={revokeLoading || !revokeShareDetails?.organizations?.length}
            className="px-4 py-2 bg-[#F47F2E] text-white rounded-lg text-sm font-medium hover:bg-[#e06a1f] transition-colors disabled:opacity-50"
          >
            {revokeSelectedUserIds.length > 0
              ? `Revoke Selected (${revokeSelectedUserIds.length})`
              : 'Revoke All'}
          </button>
        </>
      }
    >
      <div className="space-y-4">
        {/* Assessment info */}
        <div className="flex items-center gap-2 px-3 py-2 bg-slate-50 rounded-lg">
          <FiList className="text-[#F47F2E] flex-shrink-0" />
          <span className="text-sm font-medium text-[#53545a]">
            {selectedAssessment?.assessmentDescription || `Assessment #${selectedAssessment?.id}`}
          </span>
        </div>

        {revokeLoading ? (
          <div className="flex items-center justify-center py-8">
            <FiSearch className="animate-spin text-[#0077b5] text-xl mr-2" />
            <span className="text-sm text-[#919191]">Loading share details...</span>
          </div>
        ) : revokeShareDetails?.organizations?.length > 0 ? (
          <>
            <div className="flex items-center justify-between">
              <h4 className="text-sm font-medium text-[#919191]">Shared With</h4>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={onSelectAll}
                  className="text-xs font-medium text-[#0077b5] hover:text-[#005a8c] transition-colors"
                >
                  Select All
                </button>
                <span className="text-[#919191]/40">|</span>
                <button
                  type="button"
                  onClick={onDeselectAll}
                  className="text-xs font-medium text-[#0077b5] hover:text-[#005a8c] transition-colors"
                >
                  Deselect All
                </button>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Organizations list (multi-select) */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-medium text-[#919191]">
                    Organization
                    {revokeSelectedOrgIds.length > 0 && (
                      <span className="ml-2 text-[#0b79b8] font-semibold">
                        • {revokeSelectedOrgIds.length} selected
                      </span>
                    )}
                  </h3>
                </div>
                <div className="border rounded-lg border-[#919191]/30">
                  <div className="max-h-56 overflow-y-auto">
                    {revokeShareDetails.organizations.map((org) => {
                      const isOrgSelected = revokeSelectedOrgIds.includes(org.organizationId);
                      return (
                        <div
                          key={org.organizationId}
                          onClick={() => onToggleOrg(org.organizationId)}
                          className={`flex items-center gap-3 px-4 py-2.5 border-b last:border-b-0 cursor-pointer transition-colors ${
                            isOrgSelected
                              ? 'bg-[#0077b5]/10 border-[#0077b5]/20 text-[#53545a]'
                              : 'border-[#919191]/20 text-[#919191] hover:bg-slate-50'
                          }`}
                        >
                          <div className={`w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 ${
                            isOrgSelected
                              ? 'bg-[#0077b5] border-[#0077b5]'
                              : 'border-[#919191]/50 bg-white'
                          }`}>
                            {isOrgSelected && (
                              <FiCheck className="text-white text-xs" />
                            )}
                          </div>
                          <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 ${
                            isOrgSelected ? 'bg-[#0077b5]/20' : 'bg-[#919191]/10'
                          }`}>
                            <span className={`text-xs font-bold ${
                              isOrgSelected ? 'text-[#0077b5]' : 'text-[#919191]'
                            }`}>{org.organizationName?.charAt(0)?.toUpperCase()}</span>
                          </div>
                          <div className="flex-1 min-w-0">
                            <span className="text-sm">{org.organizationName}</span>
                            <span className="ml-1.5 text-xs text-[#919191]">({org.users.length} user{org.users.length !== 1 ? 's' : ''})</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>

              {/* Users list (multi-select, filtered by selected orgs) */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-medium text-[#919191]">
                    Users
                    {revokeSelectedUserIds.length > 0 && (
                      <span className="ml-2 text-[#0b79b8] font-semibold">
                        • {revokeSelectedUserIds.length} selected
                      </span>
                    )}
                  </h3>
                </div>
                {revokeSelectedOrgIds.length === 0 ? (
                  <div className="border rounded-lg border-slate-200 bg-slate-50 px-4 py-12 text-center">
                    <p className="text-sm text-slate-500">Select an organization to see users</p>
                  </div>
                ) : (
                  <div className="border rounded-lg border-slate-200">
                    <div className="max-h-56 overflow-y-auto">
                      {revokeShareDetails.organizations
                        .filter(o => revokeSelectedOrgIds.includes(o.organizationId))
                        .flatMap(o => o.users)
                        .map((user) => {
                          const isUserSelected = revokeSelectedUserIds.includes(user.userId);
                          return (
                            <div
                              key={user.userId}
                              onClick={() => onToggleUser(user.userId)}
                              className={`flex items-center gap-3 px-4 py-2.5 border-b last:border-b-0 cursor-pointer transition-colors ${
                                isUserSelected
                                  ? 'bg-[#F47F2E]/5 border-[#F47F2E]/20 text-[#53545a]'
                                  : 'border-[#919191]/20 text-[#919191] hover:bg-slate-50'
                              }`}
                            >
                              <div className={`w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 ${
                                isUserSelected
                                  ? 'bg-[#F47F2E] border-[#F47F2E]'
                                  : 'border-[#919191]/50 bg-white'
                              }`}>
                                {isUserSelected && (
                                  <FiCheck className="text-white text-xs" />
                                )}
                              </div>
                              <div className="w-7 h-7 rounded-full bg-[#F47F2E]/10 flex items-center justify-center flex-shrink-0">
                                <span className="text-xs font-bold text-[#F47F2E]">{user.userName?.charAt(0)?.toUpperCase()}</span>
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-sm text-[#53545a] truncate">{user.userName}</p>
                                {user.email && <p className="text-xs text-[#919191] truncate">{user.email}</p>}
                              </div>
                              <span className="text-xs text-[#919191] flex-shrink-0">{new Date(user.sharedAt).toLocaleDateString()}</span>
                            </div>
                          );
                        })}
                    </div>
                  </div>
                )}
              </div>
            </div>
            <p className="text-xs text-[#919191]">
              {revokeSelectedUserIds.length > 0
                ? `${revokeSelectedUserIds.length} user(s) selected — only these users will be revoked.`
                : 'No users selected — clicking "Revoke All" will remove access for everyone.'}
            </p>
          </>
        ) : (
          <div className="text-center py-6">
            <p className="text-sm text-[#919191]">No active shares found for this assessment.</p>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default RevokeAccessModal;
